import { useLayoutEffect, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';

interface ActionPopoverProps {
  open: boolean;
  /** Database cell editors share one active panel, including across tables. */
  exclusive?: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  className?: string;
  side?: 'top' | 'bottom';
  align?: 'start' | 'end';
  sideOffset?: number;
  /** Class for the wrapper element, e.g. to take it out of the layout of its parent. */
  rootClassName?: string;
  /** Runs first; calling `preventDefault` skips the default choice of what to focus. */
  onOpenAutoFocus?: (event: Event) => void;
  /** Runs first; calling `preventDefault` skips the default focus return to the trigger. */
  onCloseAutoFocus?: (event: Event) => void;
  trigger: React.ReactElement;
  children: React.ReactNode;
}

const EXCLUSIVE_OPEN_EVENT = 'noti:exclusive-cell-popover-open';
const POPOVER_OFFSET = 4;
const VIEWPORT_MARGIN = 8;

/** A floating panel with form controls, as opposed to `ActionMenu`, which holds a list of actions. */
export function ActionPopover({
  open,
  exclusive = false,
  onOpenChange,
  label,
  className,
  side = 'bottom',
  align = 'start',
  sideOffset = POPOVER_OFFSET,
  rootClassName,
  onOpenAutoFocus,
  onCloseAutoFocus,
  trigger,
  children,
}: ActionPopoverProps): React.ReactElement {
  const replaced = useRef(false);
  useLayoutEffect(() => {
    if (!exclusive || !open) return;
    document.dispatchEvent(new Event(EXCLUSIVE_OPEN_EVENT));
    const dismiss = (): void => {
      replaced.current = true;
      onOpenChange(false);
    };
    document.addEventListener(EXCLUSIVE_OPEN_EVENT, dismiss);
    return () => document.removeEventListener(EXCLUSIVE_OPEN_EVENT, dismiss);
  }, [exclusive, open, onOpenChange]);

  return (
    <div
      className={rootClassName}
      // Portalled content still bubbles React events to the host, so keep typing and clicks
      // in the panel away from page-level handlers. Escape is read from the document.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Popover.Root open={open} onOpenChange={onOpenChange}>
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={className ? `popover ${className}` : 'popover'}
            aria-label={label}
            side={side}
            align={align}
            sideOffset={sideOffset}
            collisionPadding={VIEWPORT_MARGIN}
            onOpenAutoFocus={(event) => {
              onOpenAutoFocus?.(event);
              if (event.defaultPrevented) return;
              // A search field is the first thing to type into, not the tab buttons above it.
              const field = event.currentTarget;
              if (!(field instanceof HTMLElement)) return;
              const input = field.querySelector<HTMLElement>('input:not([type="file"])');
              if (!input) return;
              event.preventDefault();
              input.focus();
            }}
            onCloseAutoFocus={(event) => {
              onCloseAutoFocus?.(event);
              // Hand focus to the new cell instead of returning to the previous trigger.
              if (replaced.current) event.preventDefault();
              replaced.current = false;
            }}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
